import { useContext, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams, useBlocker } from "react-router";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { FormStepper } from "sj-ui-toolkit/src/components";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { getChoresClient, getChoreReportClient } from "sj-ui-toolkit/src/client";
import type { AssignmentModel } from "sj-ui-toolkit/src/client/svcChores";
import { getTaxonomyClient } from "sj-ui-toolkit/src/client/svcProctor";

import { AppContext } from "@/context/AppContext";
import {
  DEFAULT_ASSESSMENT_FORM_VALUES,
  type AssessmentFormValues,
  type AssessmentFormItem,
} from "@/api/assessments";
import { useAvailableUsers } from "@/hooks/useAvailableUsers";
import { useUnsavedUsersWarning } from "@/hooks/useUnsavedUsersWarning";
import type { CategoryItem } from "@/helpers/knowledgeAssessments";

import AssessmentFormMetadata from "@/components/assessments/AssessmentFormMetadata";
import AssessmentAudience from "@/components/assessments/AssessmentAudience";
import AssessmentFormContent, {
  type OriginalValues,
} from "@/components/assessments/AssessmentFormContent";
import AssessmentFormSettings from "@/components/assessments/AssessmentFormSettings";
import AssessmentFormReview from "@/components/assessments/AssessmentFormReview";
import UserSelectionConfirmDialog from "@/components/assignments/UserSelectionConfirmDialog";
import type { DirectoryUserShape } from "@/api/assignments";
import { getDueDate } from "sj-ui-toolkit/src/utils/dates";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ValidationErrors {
  metadata: {
    name: { hasError: boolean; message: string };
    description: { hasError: boolean; message: string };
  };
  audience: { hasError: boolean; message: string };
  content: { hasError: boolean; message: string };
  settings: {
    dates: { hasError: boolean; message: string };
  };
}

const DEFAULT_VALIDATION_ERRORS: ValidationErrors = {
  metadata: {
    name: { hasError: true, message: "Assessment name is required" },
    description: { hasError: true, message: "Assessment description is required" },
  },
  audience: { hasError: true, message: "Please add at least one user" },
  content: { hasError: true, message: "Please select at least one category" },
  settings: {
    dates: { hasError: true, message: "Please select valid start and end dates" },
  },
};

// ─── Page ────────────────────────────────────────────────────────────────────

export default function KnowledgeAssessmentsNew() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const { showToast } = useToast();

  const { state } = useContext(AppContext);
  const { viewOnly } = state;
  const tenantName = getCurrentSelectedTenant() ?? "";

  const { availableUsers, allUsers, isLoading: isLoadingUsers } = useAvailableUsers(tenantName);

  const [formValues, setFormValues] = useState<AssessmentFormValues>(
    DEFAULT_ASSESSMENT_FORM_VALUES(tenantName)
  );
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [assessmentReports, setAssessmentReports] = useState<{ title: string; id: string }[]>([]);
  const [startDate, setStartDate] = useState<Date | string>("");
  const [endDate, setEndDate] = useState<Date | string>("");
  const [alreadyStarted, setAlreadyStarted] = useState(false);
  const [originalValues, setOriginalValues] = useState<OriginalValues | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
  const [validationErrors, setValidationErrors] =
    useState<ValidationErrors>(DEFAULT_VALIDATION_ERRORS);
  const [isAssessmentTitleUsed, setIsAssessmentTitleUsed] = useState(false);

  const cancelConfirmedRef = useRef(false);

  const hasLoadedRef = useRef(false);

  const {
    showUserSelectionDialog,
    selectedUsersToAdd,
    setSelectedUsersToAdd,
    selectedUsersToRemove,
    setSelectedUsersToRemove,
    addUsersAndContinueRef,
    handleSelectedUsersChange,
    handleContinue,
    handleCancel: handleCancelUserDialog,
    handleAddUsersAndContinue,
    checkUnsavedUsers,
  } = useUnsavedUsersWarning(setCurrentStep);

  // ─── Breadcrumb ──────────────────────────────────────────────────────────

  useBreadcrumb(
    isNew
      ? {
          label: "New Assessment",
          route: AdminAppRoutes.routes.assessments.routes.new.route,
          display: "page",
        }
      : formValues.listing?.title
        ? {
            label: formValues.listing.title,
            route: AdminAppRoutes.routes.assessments.routes.id.route.replace(":id", id ?? ""),
            display: "page",
          }
        : null
  );

  // ─── Available users sync ────────────────────────────────────────────────

  useEffect(() => {
    if (!isLoadingUsers) {
      setFormValues(prev => ({
        ...prev,
        target: { ...prev.target, availableUsers, allUsers },
      }));
    }
  }, [availableUsers, allUsers, isLoadingUsers]);

  // ─── Load categories ─────────────────────────────────────────────────────

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const { data, error } = await getTaxonomyClient().listSections();
        if (error) throw error;
        setCategories((data as CategoryItem[]) ?? []);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load categories";
        showToast({ severity: "error", detail: msg });
      }
    };
    loadCategories();
  }, [showToast]);

  // ─── Load existing assessment (edit) or reports (new) ───────────────────

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    if (id) {
      const loadAssessment = async () => {
        try {
          const { data, error } = await getChoresClient().getChore(id);
          if (error) throw error;
          const response = data as AssessmentFormValues & {
            items?: AssessmentFormItem[];
          };

          setOriginalValues({
            parameters: {
              allowedProgrammingLanguages:
                response.items?.[0]?.content?.knowledgeAssessment?.parameters
                  ?.allowedProgrammingLanguages ?? [],
            },
          });

          setFormValues(prev => ({
            ...(response as AssessmentFormValues),
            items: (response.items ?? []).map(item => ({
              ...item,
              content: {
                ...item.content,
                knowledgeAssessment: {
                  ...item.content?.knowledgeAssessment,
                  parameters: {
                    allowedProgrammingLanguages:
                      item.content?.knowledgeAssessment?.parameters?.allowedProgrammingLanguages ??
                      [],
                  },
                },
              },
            })),
            target: {
              ...(response.target ?? { users: [] }),
              availableUsers: prev.target.availableUsers,
              allUsers: prev.target.allUsers,
            },
          }));

          const availableAt = response.items?.[0]?.dates?.availableAt;
          if (availableAt) {
            const sd = new Date(availableAt as string);
            setStartDate(sd);
            const dueAfterDays = response.items?.[0]?.dates?.dueAfter?.days;
            if (dueAfterDays) {
              setEndDate(getDueDate(sd, dueAfterDays) as Date);
            }
            if (sd < new Date()) {
              setAlreadyStarted(true);
            }
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Failed to load assessment";
          showToast({ severity: "error", detail: msg });
        }
      };
      loadAssessment();
    } else {
      const loadReports = async () => {
        try {
          const { data, error } = await getChoreReportClient().getStatus(tenantName);
          if (error) throw error;
          setAssessmentReports(
            (data?.assignments as { title: string; id: string }[] | undefined) ?? []
          );
        } catch (err) {
          console.error("Failed to load assessment reports:", err);
        }
      };
      loadReports();
    }
  }, [id, tenantName, showToast]);

  // ─── Validate initial content (secure-coding pre-selected) ──────────────

  useEffect(() => {
    if (isNew) {
      const sections = formValues.items[0]?.content?.knowledgeAssessment?.filters?.sections ?? [];
      setValidationErrors(prev => ({
        ...prev,
        content: { hasError: sections.length === 0, message: prev.content.message },
      }));
    }
  }, []);

  // ─── Title uniqueness check ──────────────────────────────────────────────

  useEffect(() => {
    if (!assessmentReports.length || !formValues.listing?.title) {
      setIsAssessmentTitleUsed(false);
      return;
    }
    const used = assessmentReports.some(
      r =>
        r.title === formValues.listing?.title &&
        (isNew || (formValues.id && r.id !== formValues.id))
    );
    setIsAssessmentTitleUsed(used);
  }, [formValues.listing?.title, assessmentReports, isNew, formValues.id]);

  // ─── Trigger validation on review step ──────────────────────────────────

  useEffect(() => {
    if (currentStep === 4) {
      setShowValidationMessages(true);
    }
  }, [currentStep]);

  // ─── Re-validate when loaded existing (edit) ────────────────────────────

  useEffect(() => {
    if (!isNew && formValues.listing?.title) {
      validateSection("listing", formValues.listing);
      validateSection("target", formValues.target);
      if (formValues.items[0]?.content?.knowledgeAssessment) {
        validateSection("content", formValues.items[0].content.knowledgeAssessment);
      }
      if (formValues.items) {
        validateSection("items", formValues.items);
      }
    }
  }, [formValues.listing?.title, formValues.target?.users?.length]);

  // ─── Validation ───────────────────────────────────────────────────────────

  const validateSection = (section: string, values: unknown) => {
    switch (section) {
      case "listing": {
        const v = values as { title?: string; description?: string };
        const nameError = !v.title || v.title.trim().length === 0;
        const descError = !v.description || v.description.trim().length === 0;
        const nameExists =
          v.title &&
          assessmentReports.some(
            r => r.title === v.title && (isNew || (formValues.id && r.id !== formValues.id))
          );
        setValidationErrors(prev => ({
          ...prev,
          metadata: {
            name: {
              hasError: nameError || !!nameExists,
              message: nameError
                ? "Assessment name is required"
                : "An assessment with this name already exists",
            },
            description: {
              hasError: descError,
              message: "Assessment description is required",
            },
          },
        }));
        break;
      }
      case "newHire": {
        const newHireEnabled = values as boolean;
        const users = formValues.target?.users ?? [];
        setValidationErrors(prev => ({
          ...prev,
          audience: {
            ...prev.audience,
            hasError: users.length === 0 && !newHireEnabled,
          },
        }));
        break;
      }
      case "target": {
        const v = values as { users?: unknown[]; availableUsers?: unknown[] };
        const users = v.users ?? [];
        const newHireEnabled = formValues.newHire ?? false;
        setValidationErrors(prev => ({
          ...prev,
          audience: { ...prev.audience, hasError: users.length === 0 && !newHireEnabled },
        }));
        break;
      }
      case "content": {
        const v = values as { filters?: { sections?: string[] } };
        const sections = v.filters?.sections ?? [];
        setValidationErrors(prev => ({
          ...prev,
          content: { ...prev.content, hasError: sections.length === 0 },
        }));
        break;
      }
      case "items": {
        const v = values as AssessmentFormItem[];
        const dates = v[0]?.dates ?? {};
        const datesError = !dates.availableAt || !dates.dueAfter || (dates.dueAfter.days ?? 0) < 1;
        setValidationErrors(prev => ({
          ...prev,
          settings: { dates: { ...prev.settings.dates, hasError: datesError } },
        }));
        break;
      }
    }
  };

  const handleFormChange = (section: string, newValues: unknown) => {
    setFormValues(prev => {
      switch (section) {
        case "listing":
          return { ...prev, listing: newValues as AssessmentFormValues["listing"] };
        case "content": {
          const ka = newValues as AssessmentFormItem["content"]["knowledgeAssessment"];
          const item = prev.items[0];
          return {
            ...prev,
            items: [
              {
                ...(item ?? {
                  dates: { dynamic: false },
                  notify: { notifyWhenAvailable: true, sendReminders: "None" },
                }),
                content: { ...(item?.content ?? {}), knowledgeAssessment: ka },
              },
              ...prev.items.slice(1),
            ],
          };
        }
        case "target":
          return { ...prev, target: newValues as AssessmentFormValues["target"] };
        case "items":
          return { ...prev, items: newValues as AssessmentFormItem[] };
        case "newHire":
          return { ...prev, newHire: newValues as boolean };
        default:
          return prev;
      }
    });
    validateSection(section, newValues);
  };

  // ─── Step navigation ──────────────────────────────────────────────────────

  const handleStepChange = (newStep: number) => {
    if (checkUnsavedUsers(currentStep, newStep, 1)) return;
    setCurrentStep(newStep);
  };

  // ─── useBlocker ───────────────────────────────────────────────────────────

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !cancelConfirmedRef.current &&
      currentStep > 0 &&
      !submissionSuccessful &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      confirmDialog({
        message: "Changes you made will not be saved.",
        header: "Are you sure?",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        icon: "pi pi-exclamation-circle",
        acceptLabel: "Confirm",
        rejectLabel: "Cancel",
        headerStyle: { border: "none" },
        accept: () => blocker.proceed(),
        reject: () => blocker.reset(),
      });
    }
  }, [blocker]);

  // ─── isFormValid ─────────────────────────────────────────────────────────

  const isFormValid = useMemo(
    () =>
      !validationErrors.metadata.name.hasError &&
      !validationErrors.metadata.description.hasError &&
      !validationErrors.audience.hasError &&
      !validationErrors.content.hasError &&
      !validationErrors.settings.dates.hasError &&
      !isAssessmentTitleUsed,
    [validationErrors, isAssessmentTitleUsed]
  );

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!isFormValid) {
      showToast({ severity: "error", detail: "Please fix validation errors before publishing" });
      return;
    }
    try {
      setIsSubmitting(true);
      const { target, ...rest } = formValues;
      const submitData = {
        ...rest,
        target: { users: (target.users ?? []).map(u => ({ directoryUuid: u.directoryUuid })) },
      };
      await getChoresClient().assign(submitData as unknown as AssignmentModel);
      setSubmissionSuccessful(true);
      cancelConfirmedRef.current = true;
      showToast({
        severity: "success",
        summary: "Assessment Published!",
        detail:
          "You've just blazed a new trail on your Security Journey. Keep climbing toward cybersecurity mastery--next stop: summit of knowledge!",
      });
      navigate(AdminAppRoutes.routes.assessments.route);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create/update assessment";
      showToast({ severity: "error", detail: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Cancel ───────────────────────────────────────────────────────────────

  const handleCancel = () => {
    confirmDialog({
      message: "Changes you made will not be saved.",
      header: "Are you sure?",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      icon: "pi pi-exclamation-circle",
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      headerStyle: { border: "none" },
      accept: () => {
        cancelConfirmedRef.current = true;
        navigate(AdminAppRoutes.routes.assessments.route);
      },
    });
  };

  // ─── Steps ────────────────────────────────────────────────────────────────

  const steps = [
    {
      header: showValidationMessages
        ? `Name & Description${
            !validationErrors.metadata.name.hasError &&
            !validationErrors.metadata.description.hasError
              ? " ✓"
              : " ✗"
          }`
        : "Name & Description",
      title: "Assessment Name",
      content: (
        <AssessmentFormMetadata
          values={formValues.listing ?? {}}
          handleChange={newValues => handleFormChange("listing", newValues)}
          isAssessmentTitleUsed={isAssessmentTitleUsed}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.metadata}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Audience${!validationErrors.audience.hasError ? " ✓" : " ✗"}`
        : "Audience",
      title: "User Selection",
      content: (
        <AssessmentAudience
          values={formValues.target}
          handleChange={newValues => handleFormChange("target", newValues)}
          newHireValue={formValues.newHire ?? false}
          onNewHireChange={v => handleFormChange("newHire", v)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.audience}
          onSelectedUsersChange={handleSelectedUsersChange}
          onAddUsersAndContinue={addUsersAndContinueRef}
          selectedUsersToAdd={selectedUsersToAdd}
          setSelectedUsersToAdd={(users: DirectoryUserShape[]) => setSelectedUsersToAdd(users)}
          selectedUsersToRemove={selectedUsersToRemove}
          setSelectedUsersToRemove={(users: DirectoryUserShape[]) =>
            setSelectedUsersToRemove(users)
          }
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Content${!validationErrors.content.hasError ? " ✓" : " ✗"}`
        : "Content",
      title: "Assessment Categories",
      content: (
        <AssessmentFormContent
          values={
            formValues.items[0]?.content.knowledgeAssessment ?? {
              filters: { sections: [] },
              parameters: { allowedProgrammingLanguages: [] },
            }
          }
          categories={categories}
          handleChange={newValues => handleFormChange("content", newValues)}
          alreadyStarted={alreadyStarted}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.content}
          originalValues={originalValues}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `Assessment Settings${!validationErrors.settings.dates.hasError ? " ✓" : " ✗"}`
        : "Assessment Settings",
      title: "Assessment Settings",
      content: (
        <AssessmentFormSettings
          values={formValues.items}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          handleChange={newValues => handleFormChange("items", newValues)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.settings}
        />
      ),
    },
    {
      header: "Review",
      title: "Review Assessment",
      content: (
        <AssessmentFormReview
          formValues={formValues}
          categories={categories}
          startDate={startDate}
          endDate={endDate}
          onEdit={handleStepChange}
          validationErrors={validationErrors}
          isFormValid={isFormValid}
          showValidationMessages={showValidationMessages}
        />
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h1>{isNew ? "Create New Assessment" : "Update Assessment"}</h1>
        <Button icon="pi pi-times" onClick={handleCancel} severity="contrast" aria-label="Close" />
      </div>

      <div className={`p-4 min-h-[60vh] flex flex-col ${themeClasses.bgElevation1}`}>
        <div className="flex-1 flex flex-col">
          <FormStepper
            steps={steps}
            currentStep={currentStep}
            setCurrentStep={handleStepChange}
            handleSubmit={handleSubmit}
            isNew={isNew}
            hasReadOnly={viewOnly}
            isSubmitting={isSubmitting}
            submitDisabled={!isFormValid || isSubmitting}
            submitButtonText="Publish"
            saveButtonText="Save Assessment"
            loadingButtonText="Publishing..."
          />
        </div>
      </div>

      {/* Save Assessment button visible in edit mode outside review step */}
      {!isNew && currentStep !== 4 && (
        <div className="flex justify-end mt-4">
          <Button
            label={isSubmitting ? "Saving..." : "Save Assessment"}
            severity="secondary"
            disabled={isSubmitting || viewOnly}
            onClick={handleSubmit}
          />
        </div>
      )}

      <ConfirmDialog maskStyle={{ backdropFilter: "blur(5px)" }} />

      {/* useBlocker leave guard dialog */}
      <Dialog
        visible={blocker.state === "blocked"}
        onHide={() => blocker.reset?.()}
        header="Unsaved Changes"
        footer={
          <div className="flex gap-2 justify-end">
            <Button label="Cancel" severity="secondary" onClick={() => blocker.reset?.()} />
            <Button label="Leave" severity="danger" onClick={() => blocker.proceed?.()} />
          </div>
        }
      >
        <p>Changes you made will not be saved.</p>
      </Dialog>

      <UserSelectionConfirmDialog
        visible={showUserSelectionDialog}
        onHide={handleCancelUserDialog}
        onContinue={handleContinue}
        onAddUsersAndContinue={handleAddUsersAndContinue}
        selectedUsersCount={selectedUsersToAdd.length}
      />
    </>
  );
}
